import * as React from "react";

import { withLocalization } from "@shared-ui/localization-context";
import {
  PackageTypeMenuCheckboxesProps,
  PackageTypeMenuCheckboxesState,
  ILOBCheckbox,
  ILOBMap,
  IPackageTypeMap,
  IPackageType,
} from "../typings";
import { locKeys } from "../l10n";
import { UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { liveAnnounce } from "uitk-react-utils";
import { UitkPill } from "uitk-react-pill";
import { FlightTypeAdaptiveMenu } from "components/flexComponents/WizardFlightPWA/components/FlightTypeAdaptiveMenu";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { FlightType } from "src/stores/wizard/state/flight/typings";
import { Experiment as ExperimentList, isVariantEnabled } from "src/components/utility/experiment";
const {
  flightCheckboxAddLabel,
  flightCheckboxRemoveLabel,
  hotelCheckboxAddLabel,
  hotelCheckboxRemoveLabel,
  carCheckboxAddLabel,
  carCheckboxRemoveLabel,
  announcementMap,
  lobErrorHeadingsMap,
  lobErrorBodyMap,
} = locKeys;

@withLocalization
export class PackageTypeMenuCheckboxes extends React.Component<
  PackageTypeMenuCheckboxesProps,
  PackageTypeMenuCheckboxesState
> {
  constructor(props: PackageTypeMenuCheckboxesProps) {
    super(props);
    const { defaultPackageType, currentSubLOB, context } = props;
    const { formatText } = props.l10n;
    const flightsType = "f";
    const hotelsType = "h";
    const carsType = "c";
    const { flightLabel, hotelsLabel, carsLabel } = props.lobState.config.lobLabels;
    const variantEnabled = isVariantEnabled(context, ExperimentList.PWA_Packages_LOB_Added);
    const hotelLabel = variantEnabled ? formatText("wizard.packages.stay.pill.text.variant") : formatText(hotelsLabel);
    const flightsLabel = variantEnabled
      ? formatText("wizard.packages.flight.pill.text.variant")
      : formatText(flightLabel);
    const carLabel = variantEnabled ? formatText("wizard.packages.car.pill.text.variant") : formatText(carsLabel);

    /**
     * State has two map components:
     * lobMap is used for the display and control of the pills on the page
     * packageTypeDefinition is used to determine the combination for the purpose of updating the URL/stores with the package type
     * This decouples the display from the package type; swapping two pills won't change how the package type is defined
     */
    this.state = {
      lobMap: {
        hotels: {
          label: hotelLabel,
          checkedLabel: formatText("wizard.packages.staySelected.pill.text"),
          altLabelAdd: formatText(hotelCheckboxAddLabel),
          altLabelRemove: formatText(hotelCheckboxRemoveLabel),
          checked: defaultPackageType.includes(hotelsType),
          ref: React.createRef(),
          id: `package-pills-hotels`,
          type: hotelsType,
        },
        flights: {
          label: flightsLabel,
          checkedLabel: formatText("wizard.packages.flightSelected.pill.text"),
          altLabelAdd: formatText(flightCheckboxAddLabel),
          altLabelRemove: formatText(flightCheckboxRemoveLabel),
          checked: defaultPackageType.includes(flightsType),
          ref: React.createRef(),
          id: `package-pills-flights`,
          type: flightsType,
        },
        cars: {
          label: carLabel,
          checkedLabel: formatText("wizard.packages.carSelected.pill.text"),
          altLabelAdd: formatText(carCheckboxAddLabel),
          altLabelRemove: formatText(carCheckboxRemoveLabel),
          checked: defaultPackageType.includes(carsType),
          ref: React.createRef(),
          id: `package-pills-cars`,
          type: carsType,
        },
      },
      packageTypeDefinition: {
        flights: {
          checked: defaultPackageType.includes(flightsType),
          type: flightsType,
        },
        hotels: {
          checked: defaultPackageType.includes(hotelsType),
          type: hotelsType,
        },
        cars: {
          checked: defaultPackageType.includes(carsType),
          type: carsType,
        },
      },
      error: false,
      useLinkPill: true,
      activeSubLOB: currentSubLOB,
    };

    this.lobKeys = this.props.packageSubLOBs.get("pills")!;
    this.packageTypeDefinition = Object.keys(this.state.packageTypeDefinition);
  }

  componentDidMount() {
    this.setState({ useLinkPill: false });
  }

  private lobKeys: string[];
  private packageTypeDefinition: string[];

  componentDidUpdate() {
    const { currentSubLOB } = this.props;
    if (this.state.activeSubLOB !== currentSubLOB) {
      this.setState((state, props) => {
        const lobMap = state.lobMap;
        let numberOfCheckedLOBs = 0;

        this.lobKeys.forEach((lobKey) => {
          const packageType = lobKey.charAt(0);
          lobMap[lobKey].checked = currentSubLOB?.includes(packageType);

          if (lobMap[lobKey].checked) {
            numberOfCheckedLOBs += 1;
          }
        });

        const error = !Object.values(this.props.packageSubLOBs.get("lobsAllowed")!).some(
          (val) => val === currentSubLOB
        );
        let lobsMissing = 0;
        if (error) {
          lobsMissing = props.minimumLobsNeeded - numberOfCheckedLOBs;
          if (lobsMissing <= 0) {
            lobsMissing = 99;
          }
        }
        props.updateLobsMissing(lobsMissing);

        return {
          lobMap: { ...lobMap },
          activeSubLOB: props.currentSubLOB,
        };
      });
    }
  }

  flightsMapKey = "flights";

  public handleCheckboxChange = (
    lobName: string,
    lobCheckbox: React.RefObject<any>,
    checkboxId: string,
    forcePillState?: boolean
  ) => {
    const { onSubLOBSelection, l10n, updateLobsMissing, minimumLobsNeeded } = this.props;

    return () => {
      this.setState((state: PackageTypeMenuCheckboxesState) => {
        const lobMap: ILOBMap = { ...state.lobMap };
        const packageTypeDefinition: IPackageTypeMap = { ...state.packageTypeDefinition };

        // Set new pills state
        const shouldForcePillState = forcePillState !== undefined && lobName === this.flightsMapKey;

        if (shouldForcePillState) {
          lobMap[lobName].checked = forcePillState;
          packageTypeDefinition[lobName].checked = forcePillState;
        } else {
          lobMap[lobName].checked = !lobMap[lobName].checked;
          packageTypeDefinition[lobName].checked = !packageTypeDefinition[lobName].checked;
        }

        //Announce new package type
        lobMap[lobName].checked
          ? liveAnnounce(lobMap[lobName].altLabelAdd)
          : liveAnnounce(lobMap[lobName].altLabelRemove);

        const lobCombination = this.packageTypeDefinition.reduce((previousValue, key) => {
          const lob: IPackageType = lobMap[key];

          return lob.checked ? `${previousValue}${lob.type}` : previousValue;
        }, "");

        const activeSubLOB = lobCombination || "none";

        let lobsMissing = 0;
        const error = !Object.values(this.props.packageSubLOBs.get("lobsAllowed")!).some((val) => val === activeSubLOB);

        const numberOfCheckedLOBs = this.lobKeys.filter((lob) => lobMap[lob].checked).length;

        if (!error) {
          liveAnnounce(l10n.formatText(announcementMap[activeSubLOB]));
          lobsMissing = 0;
        } else {
          lobsMissing = minimumLobsNeeded - numberOfCheckedLOBs;
          if (lobsMissing <= 0) {
            lobsMissing = 99;
          }
          const errorMessage = `${l10n.formatText(lobErrorHeadingsMap[lobsMissing])} : ${l10n.formatText(
            lobErrorBodyMap[lobsMissing]
          )}`;
          liveAnnounce(errorMessage);
        }

        onSubLOBSelection(activeSubLOB)();

        //TODO: change prop name
        updateLobsMissing(lobsMissing);

        return {
          lobMap,
          packageTypeDefinition,
          error,
          currentCheckboxErrorId: error ? checkboxId : undefined,
          currentCheckboxErrorRef: error ? lobCheckbox : null,
          useLinkPill: state.useLinkPill,
        };
      });
    };
  };

  /**
   * Handle flight type selection when adaptive menu is available
   * See: https://abacus.prod.expedia.com/experiment/38435
   */
  onSelectFlightType = (flighType: FlightType) => {
    const { ref, id } = this.state.lobMap.flights;
    const pillState = true;

    this.handleCheckboxChange(this.flightsMapKey, ref, id, pillState)();
    this.props.onFlightTypeUpdate(flighType);
  };

  /** Unselect flight pill when adaptive menu clear button is clicked */
  unselectFlightPill = () => {
    const { ref, id } = this.state.lobMap.flights;
    const pillState = false;

    this.handleCheckboxChange(this.flightsMapKey, ref, id, pillState)();
  };

  renderFlightPillWithAdaptiveMenu = () => {
    const { checked, label: pillLabel } = this.state.lobMap.flights;
    const currentFlightType = this.props.setFlightType();
    const label = checked ? `${pillLabel}: ${currentFlightType}` : pillLabel;
    const flightTypeSelectorInPillExperimentName = "newFlightsinPill";

    return (
      <Experiment name={flightTypeSelectorInPillExperimentName} key={this.flightsMapKey}>
        <ExperimentControl>{this.renderPill(this.flightsMapKey, this.state.lobMap.flights)}</ExperimentControl>
        <ExperimentVariant bucket={1}>
          <FlightTypeAdaptiveMenu
            onSelectFlightType={this.onSelectFlightType}
            onClearButtonClick={this.unselectFlightPill}
            initialFlightType={currentFlightType}
            trigger={
              <UitkPill
                id={`package-pills-${this.flightsMapKey}`}
                toggleState={checked}
                children={label}
                triggerPill={true}
              />
            }
            showMulticity={this.props.showMulticity}
            showOneway={this.props.showOneway}
          />
        </ExperimentVariant>
      </Experiment>
    );
  };

  renderPill = (key: string, { checked, ref, id, label, checkedLabel }: ILOBCheckbox) => {
    const { useLinkPill } = this.state;

    const variantEnabled = isVariantEnabled(this.props.context, ExperimentList.PWA_Packages_LOB_Added);
    const overridenLabel = checked && variantEnabled ? checkedLabel : label;

    return (
      <UitkPill
        key={key}
        id={!useLinkPill ? `package-pills-${key}` : ""}
        checked={checked}
        onChange={this.handleCheckboxChange(key, ref, id)}
        children={overridenLabel}
      />
    );
  };

  pillsToLoad = () => {
    const { lobMap: lobsMap, useLinkPill } = this.state;

    return this.lobKeys.map((lobKey) => {
      const lobCheckbox: ILOBCheckbox = lobsMap[lobKey];
      const pillToLoad = this.renderPill(lobKey, lobCheckbox);

      if (this.flightsMapKey === lobKey && (this.props.showMulticity || this.props.showOneway)) {
        return this.renderFlightPillWithAdaptiveMenu();
      }

      if (useLinkPill) {
        return (
          <button key={lobKey} className="all-x-margin-one ssr-link-pill">
            {pillToLoad}
          </button>
        );
      }

      return pillToLoad;
    });
  };

  public render() {
    return (
      <UitkLayoutFlexItem className={this.props.className}>
        <div>
          <div>{this.pillsToLoad()}</div>
        </div>
      </UitkLayoutFlexItem>
    );
  }
}
